
import React, { useState, useEffect } from 'react';
import { useSettingsPanel } from '@/hooks/useSettingsPanel';
import { SettingType } from '@/types/config';
import DynamicSettingRenderer from './DynamicSettingRenderer';

interface DynamicSettingsTabProps {
  tabName: string;
}

const DynamicSettingsTab: React.FC<DynamicSettingsTabProps> = ({ tabName }) => {
  const { getTabSettings, getTabLabel } = useSettingsPanel();
  const [, setRefresh] = useState(0);

  // Listen for settings updates to trigger re-renders
  useEffect(() => {
    const handleSettingsUpdate = () => {
      setRefresh(prev => prev + 1);
    };

    window.addEventListener('settings-updated', handleSettingsUpdate);
    return () => window.removeEventListener('settings-updated', handleSettingsUpdate);
  }, []);

  const tabSettings = getTabSettings(tabName);
  const tabLabel = getTabLabel(tabName);

  if (Object.keys(tabSettings).length === 0) {
    return (
      <div className="empty-tab">
        <p className="text-sm text-muted-foreground">No settings available for {tabLabel}</p>
      </div>
    );
  }

  return (
    <div className="dynamic-settings-tab">
      <div className="setting-section">
        {Object.entries(tabSettings).map(([settingKey, setting]) => {
          // Ensure the setting has the required structure
          if (!setting || typeof setting !== 'object' || !('type' in setting)) {
            return null;
          }

          const typedSetting = setting as {
            type: SettingType;
            label: string;
            icon?: string;
            defaultValue?: boolean;
            multiple?: boolean;
            data?: Record<string, { label: string; value?: boolean }>;
          };

          return (
            <DynamicSettingRenderer
              key={settingKey}
              settingKey={settingKey}
              setting={typedSetting}
              tabName={tabName}
            />
          );
        })}
      </div>
    </div>
  );
};

export default DynamicSettingsTab;
